import { useGetList, useTranslate } from "ra-core";
import { Progress } from "@/components/ui/progress";
import { Separator } from "@/components/ui/separator";

import { formatCurrency } from "../misc/formatCurrency";
import { useConfigurationContext } from "../root/ConfigurationContext";
import type { Deal, DealOrder } from "../types";
import { day, keyExample, moment, paidPercent } from "./onecFormat";

/**
 * «1С» on a deal: its orders in 1С with the sum, what is paid, what is left
 * and when the CRM last read them. Nothing while the module is off.
 */
export const OneCBlock = ({ deal }: { deal: Deal }) => {
  const { onec } = useConfigurationContext();
  if (!onec?.enabled) return null;
  return (
    <OneCOrders
      deal={deal}
      prefix={onec.dealKeyPrefix}
      suffix={onec.dealKeySuffix}
    />
  );
};

const OneCOrders = ({
  deal,
  prefix,
  suffix,
}: {
  deal: Deal;
  prefix?: string;
  suffix?: string;
}) => {
  const translate = useTranslate();
  const { currency } = useConfigurationContext();
  const { data: orders, isPending } = useGetList<DealOrder>("deal_orders", {
    filter: { deal_id: deal.id },
    pagination: { page: 1, perPage: 50 },
    sort: { field: "date", order: "ASC" },
  });
  if (isPending) return null;
  // an order 1С no longer has (removed_at) is neither shown nor counted
  const list = (orders ?? []).filter((o) => !o.removed_at);
  const money = (n: number) => formatCurrency(Number(n) || 0, currency);
  const amount = list.reduce((s, o) => s + (Number(o.amount) || 0), 0);
  const paid = list.reduce((s, o) => s + (Number(o.paid) || 0), 0);
  const example = keyExample(prefix, suffix, deal.id);

  return (
    <div className="m-4" data-testid="onec-block">
      <Separator className="mb-4" />
      <h3 className="text-base font-semibold mb-3">
        {translate("crm.onec.title")}
      </h3>
      {list.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          {translate("crm.onec.empty", { key: example })}
        </p>
      ) : (
        <ul className="flex flex-col gap-4">
          {list.map((order) => (
            <OrderRow key={order.id} order={order} money={money} />
          ))}
        </ul>
      )}
      {list.length > 1 ? (
        <p
          className="mt-3 text-sm flex flex-wrap gap-x-2"
          data-testid="onec-total"
        >
          <span className="text-muted-foreground">
            {translate("crm.onec.total")}
          </span>
          <span className="tabular-nums">
            {translate("crm.onec.total_line", {
              paid: money(paid),
              amount: money(amount),
              rest: money(Math.max(amount - paid, 0)),
            })}
          </span>
        </p>
      ) : null}
    </div>
  );
};

const OrderRow = ({
  order,
  money,
}: {
  order: DealOrder;
  money: (n: number) => string;
}) => {
  const translate = useTranslate();
  const amount = Number(order.amount) || 0;
  const paid = Number(order.paid) || 0;
  const percent = paidPercent(paid, amount);
  const title = order.number
    ? translate("crm.onec.order", {
        number: order.number,
        date: day(order.date),
      })
    : translate("crm.onec.order_no_number", { date: day(order.date) });

  return (
    <li data-order={order.onec_ref} className="flex flex-col gap-2 min-w-0">
      <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-0.5 min-w-0">
        <span className="text-sm font-medium break-words min-w-0">{title}</span>
        <span className="text-xs text-muted-foreground">
          {translate(`crm.onec.status.${order.status}`)}
        </span>
      </div>
      <dl className="grid grid-cols-3 gap-2 text-sm min-w-0">
        <Figure label={translate("crm.onec.amount")} value={money(amount)} />
        <Figure label={translate("crm.onec.paid")} value={money(paid)} />
        <Figure
          label={translate("crm.onec.rest")}
          value={money(Math.max(amount - paid, 0))}
        />
      </dl>
      <Progress
        value={percent}
        aria-label={translate("crm.onec.progress", { percent })}
      />
      <span className="text-xs text-muted-foreground">
        {translate("crm.onec.synced", { time: moment(order.synced_at) })}
      </span>
    </li>
  );
};

const Figure = ({ label, value }: { label: string; value: string }) => (
  <div className="flex flex-col min-w-0">
    <dt className="text-xs text-muted-foreground">{label}</dt>
    <dd className="tabular-nums break-words">{value}</dd>
  </div>
);
